import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize } from 'rxjs';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { ButtonComponent, FormDataService, FormValueChangedEvent, InlineFormSchemaComponent, LoaderModule } from '@mds/ui';
import { MAT_SELECT_CONFIG } from '@angular/material/select';
import { MakersiteService } from '@services/makersite.service';
import { DataSourcesService } from './services/data-sources.service';
import { ToastrService } from '@services/toaster.service';
import {
  DataSourceFieldError,
  DataSourceFieldSchema,
  DataSourceFormDialogData,
  DataSourceSchemaResponse,
  DataSourceTestResponse
} from './models/data-source.model';
import { parseValidationErrors } from './helpers/validation-error-parser';

@Component({
  selector: 'app-data-source-form-dialog',
  standalone: true,
  imports: [CommonModule, MatDialogModule, InlineFormSchemaComponent, ButtonComponent, LoaderModule],
  templateUrl: './data-source-form-dialog.component.html',
  styleUrls: ['./data-source-form-dialog.component.scss'],
  providers: [
    DataSourcesService,
    {
      provide: FormDataService,
      useFactory: (svc: MakersiteService) => new FormDataService(svc),
      deps: [MakersiteService]
    },
    {
      provide: MAT_SELECT_CONFIG,
      useValue: { overlayPanelClass: 'options-container' }
    }
  ]
})
export class DataSourceFormDialogComponent implements OnInit {
  data: DataSourceFormDialogData = inject(MAT_DIALOG_DATA);
  private dialogRef = inject(MatDialogRef<DataSourceFormDialogComponent>);
  private dataSourcesService = inject(DataSourcesService);
  private toastr = inject(ToastrService);
  private destroyRef = inject(DestroyRef);

  /** Schema object passed to InlineFormSchemaComponent. */
  schemaInput: { formSchema: DataSourceFieldSchema[]; lines: Array<{ key: string; value: unknown }> } | null = null;

  isSubmitting = false;
  isTesting = false;
  isLoading = true;
  testResult: { success: boolean; message: string } | null = null;
  validationErrors: DataSourceFieldError[] = [];

  /** Latest form state from InlineFormSchemaComponent. */
  private formData: FormValueChangedEvent | null = null;

  /** Full schema (all connector types, with show_options applied). */
  private fullSchema: DataSourceFieldSchema[] = [];

  get isEditMode(): boolean {
    return this.data.mode === 'edit';
  }

  get title(): string {
    return this.isEditMode ? 'Edit Data Source' : 'Add Data Source';
  }

  get isFormValid(): boolean {
    return this.formData?.valid ?? false;
  }

  /** Current connector_type from form state (for submit filtering). */
  private get selectedConnectorType(): string {
    return this.formData?.value?.Inputs?.connector_type || 's3';
  }

  ngOnInit(): void {
    const parentSchema = this.data.formSchema;

    if (parentSchema?.length) {
      this.initSchema(parentSchema);
    } else {
      this.dataSourcesService
        .getFormSchema()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (res: DataSourceSchemaResponse) => {
            this.initSchema(res?.formSchema || []);
          },
          error: () => {
            this.toastr.showMessage('Failed to load form schema', 2000);
            this.dialogRef.close(false);
          }
        });
    }
  }

  onFormValueChanged(event: FormValueChangedEvent): void {
    this.formData = event;
    this.validationErrors = [];
  }

  testConnection(): void {
    if (this.isTesting) return;

    this.isTesting = true;
    this.testResult = null;

    const formValues = this.getFormInputs();
    const rid = this.getEditRid();

    this.dataSourcesService
      .testConnection(rid, formValues)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => (this.isTesting = false))
      )
      .subscribe({
        next: (res: DataSourceTestResponse) => {
          this.testResult = { success: res.success, message: res.message };
        },
        error: (err: { error?: { message?: string } }) => {
          this.testResult = { success: false, message: this.extractErrorMessage(err) };
        }
      });
  }

  submit(): void {
    if (!this.formData?.valid || this.isSubmitting) return;

    this.isSubmitting = true;
    const payload = this.buildPayload();
    this.saveDataSource(payload);
  }

  /** Filter raw form values to only relevant fields for the selected connector. */
  private buildPayload(): Record<string, unknown> {
    const raw = this.formData!.value?.Inputs ?? {};
    const allowedKeys = this.getAllowedKeys();
    const isUsable = (v: unknown): boolean => v !== '' && v != null;

    return Object.fromEntries(Object.entries(raw).filter(([k, v]) => allowedKeys.has(k) && isUsable(v)));
  }

  /** Build the set of field keys that are relevant for the current connector type. */
  private getAllowedKeys(): Set<string> {
    const connectorType = this.selectedConnectorType;
    return new Set(
      this.fullSchema
        .filter((f) => {
          if (f.formtype === 'disabled') return false;
          if (!f.connector_type) return true;
          return f.connector_type.split(',').includes(connectorType);
        })
        .map((f) => f.key)
    );
  }

  /** Dispatch create or update and handle the result. */
  private saveDataSource(payload: Record<string, unknown>): void {
    const action = this.isEditMode ? 'updated' : 'created';
    const request$ = this.isEditMode
      ? this.dataSourcesService.updateDataSource(this.data.dataSource!.rid, payload)
      : this.dataSourcesService.createDataSource(payload);

    request$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.toastr.showMessage(`Data source ${action}`);
        this.dialogRef.close(true);
      },
      error: (err: { error?: { message?: string }; status?: number }) => {
        this.isSubmitting = false;
        this.handleSaveError(err);
      }
    });
  }

  /** Handle validation and generic errors from save requests. */
  private handleSaveError(err: { error?: { message?: string }; status?: number }): void {
    const msg = this.extractValidationMessage(err);
    if (msg) {
      this.showValidationErrors(msg);
    } else {
      this.showGenericSaveError();
    }
  }

  private extractValidationMessage(err: { error?: { message?: string }; status?: number }): string | null {
    const validStatuses = [400, 422];
    const msg = err?.error?.message;
    return msg && validStatuses.includes(err.status!) ? msg : null;
  }

  private showGenericSaveError(): void {
    const action = this.isEditMode ? 'update' : 'create';
    this.toastr.showMessage(`Failed to ${action} data source`, 2000);
  }

  /** Parse validation message and either show field errors or a toast fallback. */
  private showValidationErrors(msg: string): void {
    this.validationErrors = parseValidationErrors(msg, this.fullSchema);
    if (!this.validationErrors.length) {
      this.toastr.showMessage(msg, 3000);
    }
  }

  cancel(): void {
    this.dialogRef.close(false);
  }

  private getFormInputs(): Record<string, unknown> {
    return this.formData?.value?.Inputs ?? {};
  }

  private getEditRid(): string | null {
    return this.data.dataSource?.rid ?? null;
  }

  private extractErrorMessage(err: { error?: { message?: string } }): string {
    return err?.error?.message ?? 'Connection test failed';
  }

  /**
   * Initialize from the full schema: transform connector-specific fields
   * to use show_options for reactive visibility, then build the schema input.
   */
  private initSchema(schema: DataSourceFieldSchema[]): void {
    // Transform connector-specific fields: add show_options_key/value so
    // FormDataService generates expressions.hide automatically.
    // e.g. a field with connector_type="s3" becomes:
    //   show_options_key="connector_type", show_options_value="s3"
    // Fields with connector_type="azure_blob,sharepoint" become:
    //   show_options_key="connector_type", show_options_value="azure_blob,sharepoint"
    // FormDataService supports comma-separated show_options_value.
    this.fullSchema = schema.map((field) => {
      if (field.connector_type && !field.show_options_key) {
        return {
          ...field,
          show_options_key: 'connector_type',
          show_options_value: field.connector_type
        };
      }
      return field;
    });

    const lines = this.buildLines();
    this.schemaInput = { formSchema: this.fullSchema, lines };
    this.isLoading = false;
  }

  /**
   * Convert the data source object into the { key, value } lines format
   * that InlineFormSchemaComponent uses to populate the form model.
   */
  private buildLines(): Array<{ key: string; value: unknown }> {
    const ds = this.data.dataSource;
    if (!ds) return [];

    return this.fullSchema
      .filter((field) => {
        // Password fields: always start empty (secrets aren't returned by backend)
        if (field.fieldtype === 'password') return false;
        return ds[field.key] !== undefined && ds[field.key] !== null;
      })
      .map((field) => ({
        key: field.key,
        value: ds[field.key]
      }));
  }
}
